/**
 * Settings → Notifications (D-099): reminders leave on free channels first — push on the mobile
 * app, then e-mail — and WhatsApp (paid per message since 1 October 2026) only when the agency
 * turns it on. The owner and the manager choose the order and read the log of the last sends.
 */

import {
  ApiError,
  type NotificationChannelEnum,
  type NotificationLog,
  type NotificationLogStatusEnum,
  useNotificationsLogsList,
  useNotificationsSettingsList,
  useNotificationsSettingsPartialUpdate,
} from "@agencyos/api-client";
import { Badge, type BadgeTone, Button, GlassCard, Skeleton, SoftToggle, TextField } from "@agencyos/ui-web";
import { ArrowDown, ArrowUp } from "lucide-react";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { SettingsLayout } from "../components/SettingsLayout";
import { fieldErrors, useCanEditSettings } from "../lib/settings";
import { dateTimeText, Pager } from "./billing/shared";

const CHANNELS: NotificationChannelEnum[] = ["push", "email", "whatsapp"];
const STATUS_TONE: Record<NotificationLogStatusEnum, BadgeTone> = { queued: "offer", sent: "success", failed: "danger" };

interface Draft {
  order: NotificationChannelEnum[];
  whatsapp: boolean;
  senderEmail: string;
  senderName: string;
}

function asOrder(value: unknown): NotificationChannelEnum[] {
  const known = Array.isArray(value) ? value.filter((v): v is NotificationChannelEnum => CHANNELS.includes(v as NotificationChannelEnum)) : [];
  return [...known, ...CHANNELS.filter((c) => !known.includes(c))];
}

function SettingsCard() {
  const { t } = useTranslation();
  const canEdit = useCanEditSettings();
  const list = useNotificationsSettingsList();
  const row = list.data?.status === 200 ? list.data.data.results[0] : undefined;
  const save = useNotificationsSettingsPartialUpdate({ mutation: { onSuccess: () => void list.refetch() } });
  const [draft, setDraft] = useState<Draft | null>(null);
  const errors = fieldErrors(save.error);
  if (!row) return list.isPending ? <Skeleton style={{ height: 160 }} /> : null;
  const form: Draft = draft ?? {
    order: asOrder(row.channel_order),
    whatsapp: row.whatsapp_enabled ?? false,
    senderEmail: row.sender_email ?? "",
    senderName: row.sender_name ?? "",
  };
  const move = (index: number, step: -1 | 1) => {
    const order = [...form.order];
    const [channel] = order.splice(index, 1);
    if (channel === undefined) return;
    order.splice(index + step, 0, channel);
    setDraft({ ...form, order });
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate(
      {
        id: row.id,
        data: { channel_order: form.order, whatsapp_enabled: form.whatsapp, sender_email: form.senderEmail.trim(), sender_name: form.senderName.trim() },
      },
      { onSuccess: (r) => r.status === 200 && setDraft(null) },
    );
  };

  return (
    <GlassCard as="section" className="stack" aria-labelledby="notifications-card-title">
      <h2 id="notifications-card-title">{t("notifications.card")}</h2>
      <p className="muted">{t("notifications.free")}</p>
      <form className="stack" onSubmit={onSubmit} noValidate aria-label={t("notifications.card")}>
        <fieldset className="plain-fieldset stack" disabled={!canEdit}>
          <legend>{t("notifications.order")}</legend>
          <p className="muted">{t("notifications.orderHint")}</p>
          <ol className="plain-list stack" aria-label={t("notifications.order")}>
            {form.order.map((channel, index) => (
              <li key={channel} className="row between">
                <span>
                  {index + 1}. {t(`notifications.channels.${channel}`)}
                  {channel === "whatsapp" && !form.whatsapp ? <small className="muted"> — {t("notifications.whatsappLastResort")}</small> : null}
                </span>
                <span className="row">
                  <Button variant="ghost" aria-label={t("notifications.moveUp", { channel: t(`notifications.channels.${channel}`) })} disabled={!canEdit || index === 0} onClick={() => move(index, -1)}>
                    <ArrowUp size={16} aria-hidden="true" />
                  </Button>
                  <Button variant="ghost" aria-label={t("notifications.moveDown", { channel: t(`notifications.channels.${channel}`) })} disabled={!canEdit || index === form.order.length - 1} onClick={() => move(index, 1)}>
                    <ArrowDown size={16} aria-hidden="true" />
                  </Button>
                </span>
              </li>
            ))}
          </ol>
          {errors.channel_order ? <p role="alert" className="ko">{errors.channel_order}</p> : null}
          <SoftToggle label={t("notifications.whatsapp")} checked={form.whatsapp} disabled={!canEdit} onChange={(whatsapp) => setDraft({ ...form, whatsapp })} />
          <p className="muted">{t("notifications.whatsappHint")}</p>
          <div className="form-grid">
            <TextField type="email" label={t("notifications.senderEmail")} hint={t("notifications.senderEmailHint")} value={form.senderEmail} error={errors.sender_email} onChange={(e) => setDraft({ ...form, senderEmail: e.target.value })} />
            <TextField label={t("notifications.senderName")} value={form.senderName} maxLength={80} error={errors.sender_name} onChange={(e) => setDraft({ ...form, senderName: e.target.value })} />
          </div>
        </fieldset>
        {canEdit ? (
          <div className="row">
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? t("common.loading") : t("common.save")}
            </Button>
            {save.isSuccess && !draft ? <span role="status">{t("common.saved")}</span> : null}
          </div>
        ) : null}
      </form>
    </GlassCard>
  );
}

function kindText(kind: string, t: (key: string) => string, exists: (key: string) => boolean): string {
  const key = `notifications.kinds.${kind}`;
  return exists(key) ? t(key) : kind;
}

function LogRows({ rows }: { rows: NotificationLog[] }) {
  const { t, i18n } = useTranslation();
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th scope="col">{t("notifications.log.date")}</th>
            <th scope="col">{t("notifications.log.kind")}</th>
            <th scope="col">{t("notifications.log.recipient")}</th>
            <th scope="col">{t("notifications.log.channel")}</th>
            <th scope="col">{t("notifications.log.status")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td>{dateTimeText(row.created_at, i18n.language)}</td>
              <td>{kindText(row.kind, t, (k) => i18n.exists(k))}</td>
              <td>{row.recipient || "—"}</td>
              <td>
                {row.channel ? t(`notifications.channels.${row.channel}`) : "—"}
                {row.test_mode ? (
                  <>
                    {" "}
                    <Badge tone="offer">{t("notifications.log.test")}</Badge>
                  </>
                ) : null}
              </td>
              <td>
                <Badge tone={STATUS_TONE[row.status]}>{t(`notifications.log.statuses.${row.status}`)}</Badge>
                {row.status === "failed" && row.error ? (
                  <>
                    <br />
                    <small className="muted">{row.error}</small>
                  </>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LogCard() {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const list = useNotificationsLogsList({ page });
  const data = list.data?.status === 200 ? list.data.data : undefined;
  let content;
  if (list.isPending) content = <Skeleton />;
  else if (!data) content = <p role="alert">{t("common.error")}</p>;
  else if (!data.results.length) content = <p className="muted">{t("notifications.log.empty")}</p>;
  else
    content = (
      <>
        <LogRows rows={data.results} />
        <Pager page={page} hasNext={Boolean(data.next)} onChange={setPage} labels={{ previous: t("notifications.log.previous"), next: t("notifications.log.next"), page: t("notifications.log.page", { page }) }} />
      </>
    );
  return (
    <GlassCard as="section" className="stack" aria-labelledby="notifications-log-title">
      <h2 id="notifications-log-title">{t("notifications.log.title")}</h2>
      {content}
    </GlassCard>
  );
}

export function NotificationsPage() {
  const { t } = useTranslation();
  const list = useNotificationsSettingsList();
  const forbidden = list.error instanceof ApiError && list.error.status === 403;
  return (
    <SettingsLayout title={t("notifications.title")} intro={t("notifications.intro")}>
      {forbidden ? (
        <GlassCard role="alert">{t("notifications.forbidden")}</GlassCard>
      ) : (
        <>
          <SettingsCard />
          <LogCard />
        </>
      )}
    </SettingsLayout>
  );
}
